import { useCallback, useMemo } from 'react';
import { useAuthStore } from '@/auth/auth-store';
import { useCalendarDevices } from './use-calendar-devices';
import { useDeviceAppointments } from './use-device-appointments';
import {
  mapAppointmentsToDayList,
  type DayListRow,
} from '@/scheduling/lib/calendar-mapping';
import type { CalendarDevice } from '@/types/scheduling-api';

/**
 * The Technician Worklist's data layer — PROD-2007.
 *
 * One flat list of ONE DAY's device sessions (HBOT chamber, cryo bed, …) for
 * the person who operates the machine, modelled on the Phlebotomist Order
 * Requests tab: self-fetching, behind a role gate, paginated client-side.
 *
 * **THE DAY IS A PARAMETER, AND IT IS IN THE QUERY KEY (PROD-2408).** It used
 * to be read off the clock with no way to ask for another day. The key is
 * `deviceAppointmentsQueryKey(sessionsParams)` and `sessionsParams` carries
 * `startDate`/`endDate`, so a date change remints the query instead of being
 * answered out of the previous day's cache entry. That is the property to
 * check first on any change here: a date that reached the REQUEST but not the
 * KEY would return the right rows eventually and the wrong rows instantly,
 * with nothing failing and nothing logged.
 *
 * It composes the two feeds the calendar's Devices tab already uses, and
 * nothing else:
 *
 *   1. `useCalendarDevices` — `GET /api/Resource?status=active` — the machines.
 *   2. `useDeviceAppointments` — `POST /api/Appointment/ResourceList` with the
 *      device ids in `devices` (PROD-2382). It used to be the PRACTITIONER
 *      route with the device ids in `practitioners`, which returned the right
 *      rows only because that filter matches on reference with no type check.
 *      `pageNumber: -1` (the hook's default) means "every record": the whole
 *      day lands in one response and the table pages it itself.
 *
 * What this hook does NOT own: filtering by device and pagination. Both are the
 * table's, because both are view state that must survive a refetch.
 *
 * **The zero-versus-unknown contract (PROD-2364).** `rows` is `[]` in every
 * state except `'ready'` — including while loading. A consumer must therefore
 * gate any record COUNT it renders on `state`, never on `rows.length`: a count
 * derived from the array alone would read "0 records" during the load, which is
 * precisely the defect this programme shipped once already on the Devices tab
 * badge. `state === 'no-sessions'` is the only state that means "we asked, and
 * there are none".
 *
 * **Data already on screen is never thrown away.** Two separate mechanisms
 * defend that, and both exist because this screen is read standing at a machine
 * during a shift, where a blank table is worse than a slightly stale one:
 *   - a feed counts as errored only when it has NOTHING to show
 *     (`isLoadingError`), so a failed background refetch leaves the rendered
 *     worklist alone — see the state machine below;
 *   - the sessions query keeps its previous rows while a NEW key loads
 *     (`keepPreviousData`), so adding a device in Settings does not blank the
 *     table back to a spinner.
 *
 * **…except across a date change, where keeping them would be the bug.** The
 * placeholder is refused when the key's date range moved (see
 * `use-device-appointments.ts`), so picking another day drops to `'loading'`
 * — no rows, and by the contract above no count either — rather than showing
 * one day's sessions under another day's heading. "Never throw away what is on
 * screen" is a rule about the SAME question being re-asked; a new day is a
 * different question.
 */

/** A device session row: the calendar's row plus the device metadata the worklist shows. */
export interface TechnicianSessionRow extends DayListRow {
  /** `CalendarDevice.name` — the machine, e.g. "HBOT Chamber 1". */
  deviceName: string;
  /** `CalendarDevice.technicianName` — the technician assigned to the machine. */
  deviceTechnicianName?: string;
  /**
   * End − start in whole minutes. The session window, NOT `MinutesDuration`.
   *
   * `undefined` means UNKNOWN — an unparseable timestamp, or an end that
   * precedes its start. It is NOT the same as `0`, which is a genuine
   * zero-length window and stays `0`. Clamping the unknown case to zero would
   * render bad data as a plausible "0 min"; the consumer renders `undefined`
   * as the same em-dash placeholder every other optional cell uses.
   */
  durationMinutes: number | undefined;
}

/** Which of the seven designed states the screen is in. Exactly one is true at a time. */
export type TechnicianWorklistState =
  | 'loading'
  /** No practice on the user yet — the registry will never even be asked. */
  | 'no-practice'
  | 'devices-error'
  | 'no-devices'
  | 'sessions-error'
  | 'no-sessions'
  | 'ready';

export interface TechnicianSessionsResult {
  state: TechnicianWorklistState;
  /** Active devices of the practice, in the registry's own order. */
  readonly devices: readonly CalendarDevice[];
  /** The day's sessions across every device, sorted by start time ascending. */
  readonly rows: readonly TechnicianSessionRow[];
  /** Retries the feed that failed. No-op when `state` is not an error. */
  retry: () => void;
}

/**
 * Shared empty literals — a fresh `[]` per render would remount the table.
 *
 * `readonly`, and declared `readonly` on the result type too: these singletons
 * are handed to EVERY consumer in every non-ready state, so one consumer
 * sorting its `rows` in place would reorder the empty array every other
 * consumer is holding.
 */
const NO_DEVICES: readonly CalendarDevice[] = [];
const NO_ROWS: readonly TechnicianSessionRow[] = [];

/**
 * What the Device cell reads when a session's machine is no longer in the
 * active registry. A literal, not a blank: the row is real and the technician
 * has to see it, but this surface genuinely does not know the machine's name.
 */
const UNKNOWN_DEVICE_NAME = 'Unknown device';

/**
 * Local YYYY-MM-DD for a `Date`. Same derivation as the calendar page's own
 * `toLocalISODate` (which is module-private there): `toISOString()` would
 * shift the date by a day for users east of UTC in the evening.
 */
export function toLocalISODate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Today, as local YYYY-MM-DD. EXPORTED (PROD-2408) so the screen and the hook
 * answer "what is today" the same way — the header decides whether to read
 * "Today" or a date from exactly this string, and a second derivation is how
 * a label and a query drift apart across midnight or a timezone.
 */
export function todayLocalISODate(): string {
  return toLocalISODate(new Date());
}

/** Local-midnight `Date` for a YYYY-MM-DD, or `undefined` if unreadable. */
export function parseLocalISODate(ymd: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/**
 * @param dateYmd the day to show, as local `YYYY-MM-DD`. Defaults to today,
 *   which is what the screen opens on; anything that is not a well-formed
 *   date falls back to today rather than putting a malformed range on the
 *   wire — a worklist showing today when it was asked for nonsense is
 *   recoverable, an empty day with no explanation is not.
 */
export function useTechnicianSessions(
  dateYmd?: string,
): TechnicianSessionsResult {
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';
  // Not `dateYmd ?? today`: a caller that hands over a malformed string gets
  // today too, so there is exactly one shape of date below this line.
  const dayStr =
    dateYmd && parseLocalISODate(dateYmd) ? dateYmd : todayLocalISODate();

  const devicesQuery = useCalendarDevices(practiceId);
  const devices = devicesQuery.data ?? NO_DEVICES;

  // Sorted, so the React Query key below is stable across re-renders — the
  // registry's own order is subtype-then-name, which a rename can reshuffle.
  const deviceIds = useMemo(() => devices.map((d) => d.id).sort(), [devices]);

  /**
   * Null until there is something to ask about. Zero devices is NOT "ask for
   * an empty `devices` list": that body means "no filter" and would return the
   * whole practice's day, i.e. every appointment there is.
   */
  const sessionsParams = useMemo(() => {
    if (!practiceId || deviceIds.length === 0) return null;
    return {
      organizationId: orgId,
      practiceId,
      filter: 'Day' as const,
      // THE DATE IN THE KEY. `sessionsParams` is the second segment of
      // `deviceAppointmentsQueryKey`, so these two lines are what make one
      // day's rows uncacheable as another's. Do not move the day out of here
      // and into the request body alone.
      startDate: `${dayStr}T00:00:00.000`,
      endDate: `${dayStr}T23:59:59.000`,
      devices: deviceIds,
    };
  }, [orgId, practiceId, dayStr, deviceIds]);

  /**
   * `keepPreviousData`, because `sessionsParams` is part of the query key: a
   * change to the DEVICE IDS mints a FRESH query, which is `pending`, which
   * would empty a rendered table back to the skeleton. An administrator
   * adding a machine in Settings is exactly that, and the previous rows
   * staying up for the duration of one request is strictly better than the
   * worklist blinking out mid-shift.
   *
   * IT DOES NOT APPLY ACROSS A DATE CHANGE, and that asymmetry is the point
   * (PROD-2408). `useDeviceAppointments` refuses the placeholder when the
   * key's date range moved, so picking another day drops to `'loading'` with
   * no rows — and, by the zero-versus-unknown contract above, no count —
   * instead of rendering Tuesday's sessions under Wednesday's heading. Do not
   * "simplify" this back to a bare `keepPreviousData`: the simplification is
   * the defect.
   *
   * It does not weaken the error handling below: React Query substitutes
   * placeholder data only while a query is `pending`, so a new key that FAILS
   * still resolves to `status: 'error'` with no data — `isLoadingError`, hence
   * `'sessions-error'`, hence no stale rows sitting behind an error screen.
   */
  const sessionsQuery = useDeviceAppointments(sessionsParams, {
    keepPreviousData: true,
  });

  const deviceById = useMemo(
    () => new Map(devices.map((d) => [d.id, d])),
    [devices],
  );

  const joinedRows = useMemo<readonly TechnicianSessionRow[]>(() => {
    const dayRows = mapAppointmentsToDayList(sessionsQuery.data);
    if (dayRows.length === 0) return NO_ROWS;

    const out: TechnicianSessionRow[] = [];
    let unjoined = 0;
    for (const row of dayRows) {
      // A Block Availability entry is the machine being made unavailable, not
      // a patient session — there is no one to treat and no status to advance.
      if (row.isBlock) continue;

      // DROPPED, deliberately. A row carrying NO device id at all is a mapper
      // or backend regression: this query asks for device bookings only, so
      // every row that comes back must name a machine. Nothing here is
      // recoverable — the row cannot even say which machine is missing — and a
      // blank Device cell is worse than a missing row. Contrast the registry
      // miss immediately below: the two look alike and are deliberately not
      // treated alike.
      if (!row.deviceId) continue;

      const machine = deviceById.get(row.deviceId);
      // KEPT, deliberately. A device id the registry does not know is a RACE,
      // not a regression: `useCalendarDevices` asks for `status=active`, so a
      // machine archived mid-day leaves the registry while the appointments
      // feed still returns its afternoon bookings. Dropping the row would read
      // as "No sessions booked today" in a one-device practice while patients
      // are physically queued at the machine. The session is real; only the
      // machine's NAME is unknown, so only the name falls back.
      if (!machine) unjoined++;

      out.push({
        ...row,
        deviceName: machine?.name ?? UNKNOWN_DEVICE_NAME,
        deviceTechnicianName: machine?.technicianName ?? undefined,
        // End − start. NOT `minutesDuration`: that field is documented
        // platform-wide as a work-time ESTIMATE which can legitimately be less
        // than the booked window (PROD-2357), so printing it would give a
        // duration that disagrees with the times in the same row.
        durationMinutes: windowMinutes(row.startISO, row.endISO),
      });
    }
    // Unconditional — NOT `import.meta.env.DEV`-gated. This fires on a real
    // production race, so the one environment where the warning matters is the
    // one a DEV gate silences. Whoever triages an "Unknown device" row needs
    // the line in the browser console they are actually looking at.
    if (unjoined > 0) {
      console.warn(
        `[TechnicianWorklist] ${unjoined} session(s) reference a device that is not in the active registry — shown as "${UNKNOWN_DEVICE_NAME}". The machine was most likely archived while today's bookings still stood.`,
      );
    }
    if (out.length === 0) return NO_ROWS;
    // The mapper already sorts by start; this keeps the guarantee local to the
    // hook so the worklist's ordering does not silently depend on a shared
    // mapper's internal choice. `Array.prototype.sort` is stable, so equal
    // starts keep the order the response gave them.
    out.sort(
      (a, b) => new Date(a.startISO).getTime() - new Date(b.startISO).getTime(),
    );
    return out;
  }, [sessionsQuery.data, deviceById]);

  /**
   * Precedence is deliberate and ordered: nothing downstream of a feed may be
   * reported before that feed has answered. `isPending` (not `isLoading`) is
   * what "has not answered" means here — `isLoading` is false for a query that
   * is pending but not fetching, which is every query held back by `enabled`.
   *
   * That makes `isPending` alone ambiguous, though: it is equally true of "we
   * are waiting for an answer" and of "we are never going to ask". Only the
   * second is unrecoverable — no retry and no refresh can clear it — so the two
   * are split on `isEnabled` and the second gets a state of its own.
   */
  const state = useMemo<TechnicianWorklistState>(() => {
    // "We are never going to ask": no practice on the user, so
    // `useCalendarDevices` is `enabled: false` and stays pending forever.
    // Reported as `'loading'` this was a permanent spinner with an inert
    // Retry behind it, reproducible across a refresh.
    if (devicesQuery.isPending && !devicesQuery.isEnabled) return 'no-practice';
    if (devicesQuery.isPending) return 'loading';
    // `isLoadingError`, NOT `isError`. This version of React Query reports
    // `isError` for a query that HOLDS DATA and whose background refetch just
    // failed — it models that case separately as `isRefetchError`. Using
    // `isError` meant a tab-away/tab-back over a flaky network
    // (`useCalendarDevices` has `staleTime: 30_000`; `useDeviceAppointments`
    // sets `refetchOnMount: 'always'`) replaced a technician's rendered worklist
    // with an error screen mid-shift. A feed is errored only when it has
    // nothing left to show.
    if (devicesQuery.isLoadingError) return 'devices-error';
    if (devices.length === 0) return 'no-devices';
    if (sessionsQuery.isPending) return 'loading';
    if (sessionsQuery.isLoadingError) return 'sessions-error';
    if (joinedRows.length === 0) return 'no-sessions';
    return 'ready';
  }, [
    devicesQuery.isPending,
    devicesQuery.isEnabled,
    devicesQuery.isLoadingError,
    devices.length,
    sessionsQuery.isPending,
    sessionsQuery.isLoadingError,
    joinedRows.length,
  ]);

  const devicesRefetch = devicesQuery.refetch;
  const sessionsRefetch = sessionsQuery.refetch;
  // The same `isLoadingError` reading the state machine uses, so `retry` can
  // never disagree with the screen about which feed (if any) is broken.
  const devicesFailed = devicesQuery.isLoadingError;
  const sessionsFailed = sessionsQuery.isLoadingError;
  const retry = useCallback(() => {
    // The registry first: a sessions failure downstream of a failed registry
    // is a symptom, and re-asking the feed that succeeded is pure noise.
    if (devicesFailed) {
      void devicesRefetch();
      return;
    }
    if (sessionsFailed) void sessionsRefetch();
  }, [devicesFailed, sessionsFailed, devicesRefetch, sessionsRefetch]);

  return {
    state,
    devices,
    // Rows are surfaced ONLY when the screen is ready — see the
    // zero-versus-unknown contract at the top of the file. Both branches are
    // stable references, so this ternary does not remount the table.
    rows: state === 'ready' ? joinedRows : NO_ROWS,
    retry,
  };
}

/**
 * Whole minutes between two ISO instants, or `undefined` when the window
 * cannot be read: an unparseable timestamp, or an end that precedes its start.
 *
 * It deliberately does NOT clamp to zero. A clamped unknown renders as
 * "0 min" — indistinguishable from a real zero-length session, i.e. bad data
 * dressed up as a fact. A genuine `start === end` is `0` and stays `0`, and a
 * window that crosses midnight is just arithmetic on two instants.
 */
function windowMinutes(startISO: string, endISO: string): number | undefined {
  const start = new Date(startISO).getTime();
  const end = new Date(endISO).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return undefined;
  if (end < start) return undefined;
  return Math.round((end - start) / 60_000);
}
